import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useAuthSlice } from '~/slices/auth';
import { mergeValues } from '~/utils/helpers';

interface Data {
  user_type: number;
  refresh_expiration: string;
  access_expiration: string;
  googleAuthAccountFlag?: boolean;
  ws_refresh?: string;
  ws_access?: string;
}

interface OtpTokens {
  ephemeral_token: string;
}

interface AuthContextType {
  accessFlag: boolean | null;
  userType: number | null;
  accessTokenExpiration: string | null;
  refreshTokenExpiration: string | null;
  ephemeralToken: string | null;
  googleAuthAccountFlag: boolean | null;
  slug: string | null;
  isSessionExpired: boolean;
  setCompanySlug: (slug: string) => void;
  loginOtp: (ephemeralToken: OtpTokens) => void;
  login: (data: Data, slug?: string) => void;
  logout: () => void;
  setIsSessionExpired: (isExpired: boolean) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [accessFlag, setAccessFlag] = useState<boolean | null>(null);
  const [accessTokenExpiration, setAccessTokenExpiration] = useState<
    string | null
  >(null);
  const [refreshTokenExpiration, setRefreshTokenExpiration] = useState<
    string | null
  >(null);
  const [ephemeralToken, setEphemeralToken] = useState<string | null>(null);
  const [userType, setUserType] = useState<number | null>(null);
  const [googleAuthAccountFlag, setGoogleAuthAccountFlag] = useState<
    boolean | null
  >(null);
  const [companySlug, setCompanySlug] = useState<string | null>(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  // For Backwards Compatibility
  const setCredentials = useAuthSlice().setCredentials;

  const login = (data: Data, slug?: string) => {
    // For Backwards Compatibility
    /**
     * We are using the `setCredentials` inside of this context
     * to handle syncing credentials with both our `auth` slice and this context.
     * since most of the component still rely on the `useAuth` hook for accessing
     * user credentials.
     */
    setCredentials({
      type: data.user_type,
      accessTokenExpiration: data.access_expiration,
      refreshTokenExpiration: data.refresh_expiration,
      slug: slug ?? '',
      ws: {
        token: data.ws_access ?? '',
        refreshToken: data.ws_refresh ?? '',
      },
    });

    // Store tokens in localStorage or sessionStorage
    localStorage.setItem('user_type', String(data.user_type));
    localStorage.setItem('refresh_expiration', data.refresh_expiration);
    localStorage.setItem('access_expiration', data.access_expiration);
    setAccessTokenExpiration(data.access_expiration);
    setRefreshTokenExpiration(data.refresh_expiration);
    setUserType(data.user_type);

    if (
      localStorage.getItem('refresh_expiration') &&
      localStorage.getItem('access_expiration')
    ) {
      setAccessFlag(true);
    } else {
      setAccessFlag(false);
    }

    if (data.googleAuthAccountFlag) {
      setGoogleAuthAccountFlag(data.googleAuthAccountFlag);
      localStorage.setItem(
        'is_google_oauth_only',
        String(data.googleAuthAccountFlag),
      );
    }

    if (slug) {
      setCompanySlug(slug);
      localStorage.setItem('slug', slug);
    }

    if (data.ws_access && data.ws_refresh) {
      localStorage.setItem('ws_access', data.ws_access);
      localStorage.setItem('ws_refresh', data.ws_refresh);
    }

    //clear Ephemeral token from localStorage
    setEphemeralToken(null);
    localStorage.removeItem('ephemeral_token');
  };

  const logout = () => {
    setAccessTokenExpiration(null);
    setRefreshTokenExpiration(null);
    setEphemeralToken(null);
    setCompanySlug(null);
    setGoogleAuthAccountFlag(null);
    setUserType(null);
    setAccessFlag(false);
  };

  const loginOtp = (token: OtpTokens) => {
    setEphemeralToken(token.ephemeral_token);
    // Store Ephemeral Token in localStorage or sessionStorage
    localStorage.setItem('ephemeral_token', token.ephemeral_token);
  };

  useEffect(() => {
    const storedUserType = localStorage.getItem('user_type');
    setUserType(storedUserType ? Number(storedUserType) : null);
  }, []);

  const contextValue: AuthContextType = {
    ephemeralToken,
    accessFlag,
    accessTokenExpiration,
    refreshTokenExpiration,
    userType,
    googleAuthAccountFlag,
    isSessionExpired,
    login,
    logout,
    loginOtp,
    slug: companySlug,
    setCompanySlug,
    setIsSessionExpired,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  // Backwards Compatibility
  /**
   * We are using the `useAuthSlice` in syncing credentials with both our `auth` slice and this context.
   * since most of the component still rely on the `useAuth` hook for accessing
   * user credentials.
   */
  const auth = useAuthSlice().auth;
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('Unauthorized!, You must login first.');
  }
  const mergedValues = mergeValues(context, {
    userType: auth.user.type,
    accessFlag: auth.user.isAccessAllowed,
    accessTokenExpiration: auth.user.accessTokenExpiration,
    refreshTokenExpiration: auth.user.refreshTokenExpiration,
    googleAuthAccountFlag: auth.user.isGoogleAccount,
    isSessionExpired: auth.user.isSessionExpired,
    slug: auth.user.slug,
  } as Partial<AuthContextType>);
  return mergedValues;
};
